/**
 * PublicLanding.jsx — Artizan public-facing landing page
 * Shown at artizancraft.app/ to unauthenticated visitors.
 * Includes a "Log in" link to /login (which renders the existing Auth.jsx).
 *
 * Converted from artizan-landing-v3.html (May 2026).
 * All visual styles preserved via inline style objects.
 */

import React, { useEffect } from 'react';

// ──────────────────────────────────────────────────────────────────────
// Style tokens — kept inline so this file is self-contained and doesn't
// require touching index.css or App.css.
// ──────────────────────────────────────────────────────────────────────
const T = {
  bg: '#0a0907',
  bgSoft: '#11100d',
  bgDeep: '#060604',
  surface: '#161410',
  line: '#2a251c',
  lineSoft: '#1c1915',
  cream: '#f3ebd9',
  creamDim: 'rgba(243,235,217,0.62)',
  creamFaint: 'rgba(243,235,217,0.38)',
  gold: '#c9a35e',
  goldWarm: '#b8934a',
  goldDeep: '#8a6f3e',
  serif: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
  sans: "'Outfit', 'Helvetica Neue', system-ui, sans-serif",
  mono: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
};

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,500&family=Outfit:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';

export default function PublicLanding() {
  // Inject Google Fonts + global reset styles + scroll behavior on mount.
  // Cleanup on unmount so we don't pollute the global page if the user logs in.
  useEffect(() => {
    // Preconnect for performance
    const pre1 = document.createElement('link');
    pre1.rel = 'preconnect';
    pre1.href = 'https://fonts.googleapis.com';
    const pre2 = document.createElement('link');
    pre2.rel = 'preconnect';
    pre2.href = 'https://fonts.gstatic.com';
    pre2.crossOrigin = 'anonymous';

    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = FONTS_HREF;
    fontLink.dataset.artizanLanding = 'true';

    const globalStyle = document.createElement('style');
    globalStyle.dataset.artizanLanding = 'true';
    globalStyle.textContent = `
      html, body { margin: 0; padding: 0; background: ${T.bg}; color: ${T.cream}; }
      html { scroll-behavior: smooth; }
      ::selection { background: ${T.gold}; color: ${T.bg}; }
      .artizan-landing * { box-sizing: border-box; }
      .artizan-landing a { text-decoration: none; }
      .artizan-landing .reveal {
        opacity: 0;
        transform: translateY(24px);
        transition: opacity 0.8s ease, transform 0.8s ease;
      }
      .artizan-landing .reveal.in {
        opacity: 1;
        transform: translateY(0);
      }
      .artizan-landing .reveal-d2 { transition-delay: 0.1s; }
      .artizan-landing .reveal-d3 { transition-delay: 0.2s; }
      .artizan-landing .reveal-d4 { transition-delay: 0.3s; }
      @keyframes artizanPulse {
        0%, 100% { box-shadow: 0 0 0 4px rgba(201,163,94,0.18); }
        50% { box-shadow: 0 0 0 8px rgba(201,163,94,0.05); }
      }
      .artizan-landing .beta-bar-dot { animation: artizanPulse 2.4s ease-in-out infinite; }
      .artizan-landing h1, .artizan-landing h2, .artizan-landing h3, .artizan-landing h4 { margin: 0; }
      .artizan-landing ul { list-style: none; padding: 0; margin: 0; }
      .artizan-landing .btn:hover.btn-ghost { border-color: ${T.gold}; color: ${T.gold}; }
      .artizan-landing .btn:hover.btn-gold { background: ${T.goldWarm}; border-color: ${T.goldWarm}; }
      .artizan-landing .btn:hover.btn-link { color: ${T.gold}; }
      .artizan-landing .footer-col a:hover { color: ${T.gold}; }
      .artizan-landing .audience-item:hover { background: ${T.bgSoft}; }
      .artizan-landing .tool:hover { background: ${T.bgSoft}; }
      .artizan-landing .cta-form input:focus { outline: none; border-color: ${T.gold}; }
      .artizan-landing .toolkit-note a { color: ${T.gold}; border-bottom: 1px solid ${T.goldDeep}; }
      .artizan-landing .beta-bar a { color: ${T.gold}; border-bottom: 1px solid ${T.goldDeep}; padding-bottom: 1px; }
      @media (max-width: 900px) {
        .artizan-landing .hero-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
        .artizan-landing .hero h1 { font-size: clamp(44px, 11vw, 72px) !important; }
        .artizan-landing .hero-lead { font-size: 18px !important; }
        .artizan-landing .hero-stats { grid-template-columns: 1fr 1fr !important; gap: 24px !important; }
        .artizan-landing .section-head { grid-template-columns: 1fr !important; gap: 16px !important; margin-bottom: 48px !important; }
        .artizan-landing .trust-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
        .artizan-landing .trust-image { position: relative !important; top: 0 !important; aspect-ratio: 16/10 !important; }
        .artizan-landing .nav-link { display: none !important; }
        .artizan-landing .footer-grid { grid-template-columns: 1fr 1fr !important; }
      }
      @media (max-width: 1100px) {
        .artizan-landing .audience-grid { grid-template-columns: 1fr 1fr !important; }
      }
      @media (max-width: 1000px) {
        .artizan-landing .toolkit-grid { grid-template-columns: 1fr !important; }
      }
      @media (max-width: 900px) {
        .artizan-landing section { padding: 80px 0 !important; }
        .artizan-landing .hero { padding: 70px 0 60px !important; }
        .artizan-landing .trans-row { grid-template-columns: 1fr !important; gap: 16px !important; padding: 32px 0 !important; }
        .artizan-landing .trans-num { padding-top: 0 !important; }
      }
      @media (max-width: 720px) { .artizan-landing .wrap { padding: 0 20px !important; } }
      @media (max-width: 600px) {
        .artizan-landing .audience-grid { grid-template-columns: 1fr !important; }
        .artizan-landing .footer-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
      }
    `;

    document.head.appendChild(pre1);
    document.head.appendChild(pre2);
    document.head.appendChild(fontLink);
    document.head.appendChild(globalStyle);

    // IntersectionObserver for scroll-reveal animations
    const elements = document.querySelectorAll('.artizan-landing .reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    elements.forEach((el) => io.observe(el));

    return () => {
      // Cleanup: remove injected head elements
      document.querySelectorAll('[data-artizan-landing]').forEach((n) => n.remove());
      io.disconnect();
    };
  }, []);

  const goToLogin = (e) => {
    e?.preventDefault?.();
    window.location.assign('/login');
  };

  const handleBetaSubmit = (e) => {
    e.preventDefault();
    // For now: simple alert. Future: wire to Supabase beta_signups table or Resend.
    alert("Thanks — we'll be in touch as access opens.");
  };

  return (
    <div
      className="artizan-landing"
      style={{
        background: T.bg,
        color: T.cream,
        fontFamily: T.sans,
        fontWeight: 300,
        lineHeight: 1.5,
        WebkitFontSmoothing: 'antialiased',
        overflowX: 'hidden',
        minHeight: '100vh',
      }}
    >
      {/* ───────── Header ───────── */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(10,9,7,0.78)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${T.lineSoft}`,
        }}
      >
        <div style={wrapStyle}>
          <nav style={navStyle}>
            <a href="/" style={brandStyle}>
              <div style={brandMarkStyle}>A</div>
              <div style={brandNameStyle}>Artizan</div>
            </a>
            <div style={navActionsStyle}>
              <a href="#who" className="btn btn-link nav-link" style={btnLink}>For You</a>
              <a href="#how" className="btn btn-link nav-link" style={btnLink}>How It Works</a>
              <a href="#toolkit" className="btn btn-link nav-link" style={btnLink}>Toolkit</a>
              <a href="#trust" className="btn btn-link nav-link" style={btnLink}>Trust &amp; Safety</a>
              <a href="/login" onClick={goToLogin} className="btn btn-ghost" style={btnGhost}>Log In</a>
              <a href="#beta" className="btn btn-gold" style={btnGold}>Request Access</a>
            </div>
          </nav>
        </div>
      </header>

      {/* ───────── Hero ───────── */}
      <section style={{ padding: '120px 0 100px', position: 'relative', overflow: 'hidden' }} className="hero">
        <div style={wrapStyle}>
          <div style={heroGridStyle} className="hero-grid">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 36 }} className="reveal">
                <span style={{ width: 60, height: 1, background: T.gold }} />
                <span style={eyebrowStyle}>Artizan Studio LLC · Est. 2026</span>
              </div>
              <h1 style={heroH1Style} className="reveal reveal-d2">
                Where <em style={{ fontStyle: 'italic', fontWeight: 400, color: T.gold }}>verified</em><br />creatives connect.
              </h1>
              <p style={heroLeadStyle} className="hero-lead reveal reveal-d3">
                A verified marketplace and connected toolkit for creative professionals. Artisans build careers, Venues hire, Industry Pros discover, and audiences support — on a platform designed for the long form of creative work.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 56 }} className="reveal reveal-d4">
                <a href="#beta" className="btn btn-gold" style={btnGold}>Request beta access →</a>
                <a href="#how" className="btn btn-ghost" style={btnGhost}>See how it works</a>
              </div>
              <div style={heroStatsStyle} className="hero-stats reveal reveal-d4">
                {[
                  ['04', 'Account types'],
                  ['03', 'Member tools'],
                  ['02', 'Platforms'],
                  ['PA', 'Bethlehem · 2026'],
                ].map(([num, label]) => (
                  <div key={label}>
                    <div style={{ fontFamily: T.serif, fontSize: 32, fontWeight: 400, color: T.gold, lineHeight: 1, marginBottom: 8 }}>{num}</div>
                    <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.creamFaint }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={heroVisualStyle} className="reveal reveal-d3">
              <img
                src="https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=900&q=80&auto=format&fit=crop"
                alt="A photographer at work in studio"
                style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(0.15) brightness(0.85) sepia(0.08)' }}
              />
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(180deg, rgba(10,9,7,0) 0%, rgba(10,9,7,0.5) 100%)',
                pointerEvents: 'none',
              }} />
              <div style={{ position: 'absolute', bottom: 24, left: 24, zIndex: 2, color: T.cream, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                Studio · No. 01
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Beta Bar ───────── */}
      <div style={{ background: T.bgSoft, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`, padding: '20px 0' }} className="beta-bar">
        <div style={{ ...wrapStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, textAlign: 'center', flexWrap: 'wrap' }}>
          <span className="beta-bar-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: T.gold, boxShadow: '0 0 0 4px rgba(201,163,94,0.18)', display: 'inline-block' }} />
          <span style={{ fontSize: 13, color: T.creamDim, letterSpacing: '0.02em' }}>
            <strong style={{ color: T.cream, fontWeight: 500 }}>Currently in private beta.</strong>{' '}
            Public sign-ups open by invitation.{' '}
            <a href="#beta">Request access →</a>
          </span>
        </div>
      </div>

      {/* ───────── Mission ───────── */}
      <section style={{ padding: '120px 0', borderTop: `1px solid ${T.lineSoft}` }} className="mission">
        <div style={wrapStyle}>
          <div style={sectionHeadStyle} className="section-head reveal">
            <span style={{ fontFamily: T.serif, fontSize: 14, fontStyle: 'italic', color: T.gold }}>i. The work</span>
            <h2 style={sectionTitleStyle}>A quiet platform for serious creative careers.</h2>
          </div>
          <p style={missionBodyStyle} className="reveal reveal-d2">
            Artizan exists because the open internet stopped working for creative professionals. The feeds are noisy, the bots are loud, and the gatekeeping is misaligned. We built a verified marketplace and discovery layer that puts the <em style={{ color: T.gold, fontStyle: 'italic' }}>portfolio at the center</em> — not the algorithm. Every account is verified before it appears. Every interaction is documented, moderated, and owned by the creator who made the work.
          </p>
          <div style={{ marginTop: 56, display: 'flex', alignItems: 'center', gap: 16 }} className="reveal reveal-d3">
            <span style={{ width: 40, height: 1, background: T.goldDeep }} />
            <span style={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.creamFaint }}>
              Artizan Studio LLC · Bethlehem, Pennsylvania
            </span>
          </div>
        </div>
      </section>

      {/* ───────── For Whom (Account Types) ───────── */}
      <section id="who" style={{ padding: '120px 0', background: T.bgSoft, borderTop: `1px solid ${T.lineSoft}`, borderBottom: `1px solid ${T.lineSoft}` }} className="audience">
        <div style={wrapStyle}>
          <div style={sectionHeadStyle} className="section-head reveal">
            <span style={{ fontFamily: T.serif, fontSize: 14, fontStyle: 'italic', color: T.gold }}>ii. For whom</span>
            <h2 style={sectionTitleStyle}>Four kinds of people. <em style={{ fontStyle: 'italic', color: T.gold }}>One verified network.</em></h2>
          </div>
          <div style={audienceGridStyle} className="audience-grid reveal">
            {AUDIENCE.map((a) => (
              <div key={a.tag} className="audience-item" style={audienceItemStyle}>
                <div style={{ fontFamily: T.mono, fontSize: 11, color: T.gold, letterSpacing: '0.06em' }}>{a.tag}</div>
                <h3 style={{ fontFamily: T.serif, fontSize: 32, fontWeight: 300, lineHeight: 1.1, letterSpacing: '-0.01em' }}>{a.name}</h3>
                <p style={{ fontSize: 14, color: T.creamDim, lineHeight: 1.6 }}>{a.desc}</p>
                <ul style={{ marginTop: 8, paddingTop: 12, borderTop: `1px solid ${T.lineSoft}` }}>
                  {a.bullets.map((b, i) => (
                    <li key={i} style={{ fontSize: 12, color: T.creamFaint, padding: '4px 0', display: 'flex', gap: 8 }}>
                      <span style={{ color: T.gold }}>·</span>{b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── How It Works (Transaction Types) ───────── */}
      <section id="how" style={{ padding: '120px 0' }} className="transactions">
        <div style={wrapStyle}>
          <div style={sectionHeadStyle} className="section-head reveal">
            <span style={{ fontFamily: T.serif, fontSize: 14, fontStyle: 'italic', color: T.gold }}>iii. How it works</span>
            <h2 style={sectionTitleStyle}>Four ways to <em style={{ fontStyle: 'italic', color: T.gold }}>transact and build.</em></h2>
          </div>
          <div>
            {TRANSACTIONS.map((tx, i) => (
              <article
                key={tx.name}
                className={`trans-row reveal${i > 0 ? ` reveal-d${Math.min(i + 1, 4)}` : ''}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr 1fr',
                  gap: 48,
                  padding: '48px 0',
                  borderTop: i === 0 ? `1px solid ${T.line}` : `1px solid ${T.lineSoft}`,
                  borderBottom: i === TRANSACTIONS.length - 1 ? `1px solid ${T.line}` : 'none',
                  alignItems: 'start',
                }}
              >
                <div className="trans-num" style={{ fontFamily: T.serif, fontSize: 14, fontStyle: 'italic', color: T.gold, paddingTop: 6 }}>
                  {tx.num}
                </div>
                <div>
                  <h3 style={{ fontFamily: T.serif, fontSize: 'clamp(28px, 3vw, 40px)', fontWeight: 300, lineHeight: 1, letterSpacing: '-0.015em', marginBottom: 12 }}>{tx.name}</h3>
                  <p style={{ fontSize: 15, color: T.creamDim, lineHeight: 1.65, maxWidth: 480 }}>{tx.desc}</p>
                  <span style={{ fontFamily: T.mono, fontSize: 12, color: T.gold, marginTop: 16, padding: '8px 14px', border: `1px solid ${T.line}`, display: 'inline-block', letterSpacing: '0.04em' }}>{tx.fee}</span>
                </div>
                <div>
                  <p style={{ fontSize: 15, color: T.creamDim, lineHeight: 1.65, maxWidth: 480 }}>
                    <em style={{ fontStyle: 'italic' }}>Who uses it:</em> {tx.who}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Toolkit (Member Tools) ───────── */}
      <section id="toolkit" style={{ padding: '120px 0', background: T.bgDeep, borderTop: `1px solid ${T.lineSoft}`, position: 'relative' }} className="toolkit">
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at 20% 0%, rgba(201,163,94,0.05) 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(201,163,94,0.03) 0%, transparent 50%)',
        }} />
        <div style={{ ...wrapStyle, position: 'relative' }}>
          <div style={sectionHeadStyle} className="section-head reveal">
            <span style={{ fontFamily: T.serif, fontSize: 14, fontStyle: 'italic', color: T.gold }}>iv. The toolkit</span>
            <h2 style={sectionTitleStyle}>Three tools. <em style={{ fontStyle: 'italic', color: T.gold }}>The full creative arc.</em></h2>
          </div>
          <div style={{ maxWidth: 720, marginBottom: 80 }} className="reveal reveal-d2">
            <p style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 300, lineHeight: 1.55, color: T.creamDim }}>
              Artizan members at the Artist Studio tier get a connected toolkit that takes a project from <em style={{ color: T.gold, fontStyle: 'italic' }}>first sketch to final delivery</em> — pre-production through post — without leaving the network where your collaborators already live.
            </p>
          </div>
          <div style={toolkitGridStyle} className="toolkit-grid reveal reveal-d3">
            {TOOLS.map((tool) => (
              <div key={tool.name} className="tool" style={toolStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 28 }}>
                  <span style={{ fontFamily: T.mono, fontSize: 11, color: T.gold, letterSpacing: '0.06em' }}>{tool.tag}</span>
                  <span style={{ fontFamily: T.mono, fontSize: 9, color: T.creamFaint, letterSpacing: '0.18em', textTransform: 'uppercase' }}>{tool.phase}</span>
                </div>
                <h3 style={{ fontFamily: T.serif, fontSize: 'clamp(40px, 4vw, 56px)', fontWeight: 300, lineHeight: 0.95, letterSpacing: '-0.02em', marginBottom: 16 }}>{tool.name}</h3>
                <div style={{ fontFamily: T.serif, fontStyle: 'italic', fontSize: 17, color: T.gold, marginBottom: 24, lineHeight: 1.4 }}>{tool.tagline}</div>
                <p style={{ fontSize: 14, color: T.creamDim, lineHeight: 1.65, marginBottom: 28, maxWidth: 420 }}>{tool.desc}</p>
                <ul style={{ marginTop: 'auto', paddingTop: 24, borderTop: `1px solid ${T.lineSoft}` }}>
                  {tool.features.map((f, i) => (
                    <li key={i} style={{ fontFamily: T.mono, fontSize: 11, color: T.cream, padding: '6px 0', display: 'flex', alignItems: 'center', gap: 12, letterSpacing: '0.01em' }}>
                      <span style={{ width: 3, height: 3, background: T.gold, borderRadius: '50%', flexShrink: 0 }} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 40, textAlign: 'center', fontFamily: T.serif, fontStyle: 'italic', fontSize: 15, color: T.creamFaint }} className="toolkit-note reveal reveal-d4">
            The full toolkit is included with Artist Studio membership ($19/mo). <a href="#beta">Request beta access →</a>
          </p>
        </div>
      </section>

      {/* ───────── Lite-but-Whole ───────── */}
      <section style={{ padding: '120px 0', background: T.bg, borderTop: `1px solid ${T.lineSoft}` }} className="lite">
        <div style={wrapStyle}>
          <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }} className="reveal">
            <div style={{ ...eyebrowStyle, marginBottom: 32 }}>v. The Year 1 promise</div>
            <h2 style={{ fontFamily: T.serif, fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 300, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 28 }}>
              Lite, but <em style={{ fontStyle: 'italic', color: T.gold }}>whole.</em>
            </h2>
            <p style={{ fontFamily: T.serif, fontSize: 'clamp(17px, 1.7vw, 19px)', color: T.creamDim, lineHeight: 1.65, marginBottom: 16 }}>
              Every account type ships on Day 1. Every transaction type works. Every Member Tool is usable.
            </p>
            <p style={{ fontFamily: T.serif, fontSize: 'clamp(17px, 1.7vw, 19px)', color: T.creamDim, lineHeight: 1.65, marginBottom: 16 }}>
              What's smaller at launch is feature <strong style={{ color: T.cream, fontWeight: 500, fontStyle: 'normal' }}>depth</strong>, not feature <strong style={{ color: T.cream, fontWeight: 500, fontStyle: 'normal' }}>reach</strong>. Nobody is locked out. The deepest features expand across the next two years.
            </p>
            <p style={{ fontSize: 15, color: T.creamFaint, marginTop: 32, fontFamily: T.serif }}>
              Trust &amp; Safety, however, ships at full functionality from Day 1. That part is never Lite.
            </p>
          </div>
        </div>
      </section>

      {/* ───────── Trust & Safety ───────── */}
      <section id="trust" style={{ padding: '120px 0', background: T.bgSoft, borderTop: `1px solid ${T.lineSoft}` }} className="trust">
        <div style={wrapStyle}>
          <div style={sectionHeadStyle} className="section-head reveal">
            <span style={{ fontFamily: T.serif, fontSize: 14, fontStyle: 'italic', color: T.gold }}>vi. The standard</span>
            <h2 style={sectionTitleStyle}>Verified, moderated, <em style={{ fontStyle: 'italic', color: T.gold }}>and accountable.</em></h2>
          </div>
          <div style={trustGridStyle} className="trust-grid">
            <div style={trustImageStyle} className="trust-image reveal">
              <img
                src="https://images.unsplash.com/photo-1493722054243-2e0fcb29c5f7?w=900&q=80&auto=format&fit=crop"
                alt="A considered creative workspace"
                style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(0.3) brightness(0.7) sepia(0.12)' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }} className="reveal reveal-d2">
              {TRUST.map((t, i) => (
                <div key={t.name} style={{ padding: '28px 0', borderBottom: i === TRUST.length - 1 ? 'none' : `1px solid ${T.lineSoft}`, paddingTop: i === 0 ? 0 : 28 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 10 }}>
                    <span style={{ width: 6, height: 6, background: T.gold, flexShrink: 0, transform: 'rotate(45deg)', marginTop: 8 }} />
                    <h3 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, letterSpacing: '-0.01em' }}>{t.name}</h3>
                  </div>
                  <p style={{ fontSize: 14, color: T.creamDim, lineHeight: 1.65, paddingLeft: 20 }}>{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section id="beta" style={{ padding: '120px 0', textAlign: 'center', borderTop: `1px solid ${T.lineSoft}`, background: T.bgSoft }} className="cta">
        <div style={wrapStyle}>
          <div style={{ ...eyebrowStyle, marginBottom: 24 }} className="reveal">vii. Private beta</div>
          <h2 style={{ fontFamily: T.serif, fontSize: 'clamp(48px, 7vw, 100px)', fontWeight: 300, lineHeight: 1, letterSpacing: '-0.03em', marginBottom: 36, maxWidth: '14ch', marginLeft: 'auto', marginRight: 'auto' }} className="reveal reveal-d2">
            Request <em style={{ fontStyle: 'italic', color: T.gold }}>access.</em>
          </h2>
          <p style={{ fontFamily: T.serif, fontSize: 20, color: T.creamDim, marginBottom: 48, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }} className="reveal reveal-d3">
            Artizan is currently invite-only as we onboard our first cohort of verified members. Leave your email and we'll be in touch as access opens.
          </p>
          <form onSubmit={handleBetaSubmit} style={{ display: 'flex', gap: 8, maxWidth: 480, margin: '0 auto' }} className="cta-form reveal reveal-d3">
            <input
              type="email"
              placeholder="your@email.com"
              required
              style={{
                flex: 1, background: 'transparent', border: `1px solid ${T.line}`,
                color: T.cream, padding: '14px 18px', borderRadius: 2,
                fontFamily: T.sans, fontSize: 14, transition: 'border-color 0.25s ease',
              }}
            />
            <button type="submit" className="btn btn-gold" style={{ ...btnGold, cursor: 'pointer' }}>Request →</button>
          </form>
        </div>
      </section>

      {/* ───────── Footer ───────── */}
      <footer style={{ background: T.bg, borderTop: `1px solid ${T.line}`, padding: '80px 0 40px' }}>
        <div style={wrapStyle}>
          <div style={footerGridStyle} className="footer-grid">
            <div>
              <a href="/" style={{ ...brandStyle, marginBottom: 24 }}>
                <div style={brandMarkStyle}>A</div>
                <div style={brandNameStyle}>Artizan</div>
              </a>
              <p style={{ fontSize: 14, color: T.creamDim, lineHeight: 1.65, maxWidth: 420, marginTop: 24 }}>
                A verified marketplace and connected toolkit for creative professionals. A platform of Artizan Studio LLC, a Pennsylvania limited liability company headquartered in Bethlehem, PA.
              </p>
            </div>
            <FooterCol title="Network" links={[
              ['Artizan', '#how'], ['Aisle', '#how'], ['Vizion', '#toolkit'],
              ['Krew', '#toolkit'], ['Curtainz', '#toolkit'], ['Log in', '/login'],
            ]} onLoginClick={goToLogin} />
            <FooterCol title="Trust" links={[
              ['Terms of Service', '/terms'], ['Privacy Policy', '/privacy'],
              ['DMCA Policy', '/dmca'], ['Community Guidelines', '/community'],
            ]} />
            <FooterCol title="Company" links={[
              ['hello@artizancraft.app', 'mailto:hello@artizancraft.app'],
              ['legal@artizancraft.app', 'mailto:legal@artizancraft.app'],
              ['trust@artizancraft.app', 'mailto:trust@artizancraft.app'],
            ]} />
          </div>
          <div style={{ borderTop: `1px solid ${T.lineSoft}`, paddingTop: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ fontSize: 12, color: T.creamFaint, letterSpacing: '0.02em' }}>
              © 2026 Artizan Studio LLC · 715 Aaron Street, Bethlehem, PA 18015
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <a href="/terms" style={{ fontSize: 12, color: T.creamFaint, letterSpacing: '0.02em' }} className="footer-col">Terms</a>
              <a href="/privacy" style={{ fontSize: 12, color: T.creamFaint, letterSpacing: '0.02em' }} className="footer-col">Privacy</a>
              <a href="/dmca" style={{ fontSize: 12, color: T.creamFaint, letterSpacing: '0.02em' }} className="footer-col">DMCA</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Footer column subcomponent
// ──────────────────────────────────────────────────────────────────────
function FooterCol({ title, links, onLoginClick }) {
  return (
    <div className="footer-col">
      <h4 style={{ fontFamily: T.sans, fontSize: 11, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: T.gold, marginBottom: 20 }}>{title}</h4>
      <ul>
        {links.map(([label, href]) => (
          <li key={label} style={{ marginBottom: 10 }}>
            <a
              href={href}
              onClick={label === 'Log in' && onLoginClick ? onLoginClick : undefined}
              style={{ fontSize: 14, color: T.creamDim, transition: 'color 0.2s ease' }}
            >
              {label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Style objects
// ──────────────────────────────────────────────────────────────────────
const wrapStyle = { maxWidth: 1280, margin: '0 auto', padding: '0 32px' };

const navStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 76 };
const brandStyle = { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: T.cream };
const brandMarkStyle = {
  width: 36, height: 36, background: `linear-gradient(145deg, ${T.gold}, ${T.goldWarm})`,
  borderRadius: 4, display: 'grid', placeItems: 'center',
  fontFamily: T.serif, fontSize: 22, fontWeight: 600, color: T.bg, letterSpacing: '-0.02em',
};
const brandNameStyle = { fontFamily: T.serif, fontSize: 22, fontWeight: 500, letterSpacing: '-0.01em' };
const navActionsStyle = { display: 'flex', alignItems: 'center', gap: 8 };
const btnLink = { color: T.cream, background: 'transparent', border: 'none', padding: '11px 8px', fontSize: 13, fontFamily: T.sans, fontWeight: 500, letterSpacing: '0.04em', cursor: 'pointer', textDecoration: 'none', transition: 'color 0.25s ease' };
const btnGhost = { color: T.cream, background: 'transparent', border: `1px solid ${T.line}`, fontSize: 13, fontFamily: T.sans, fontWeight: 500, letterSpacing: '0.04em', padding: '11px 22px', borderRadius: 2, cursor: 'pointer', textDecoration: 'none', transition: 'all 0.25s ease' };
const btnGold = { color: T.bg, background: T.gold, border: `1px solid ${T.gold}`, fontSize: 13, fontFamily: T.sans, fontWeight: 500, letterSpacing: '0.04em', padding: '11px 22px', borderRadius: 2, cursor: 'pointer', textDecoration: 'none', transition: 'all 0.25s ease' };

const eyebrowStyle = { fontFamily: T.sans, fontSize: 11, fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', color: T.gold };

const heroGridStyle = { display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', gap: 60, alignItems: 'end' };
const heroH1Style = { fontFamily: T.serif, fontWeight: 300, fontSize: 'clamp(56px, 8vw, 124px)', lineHeight: 0.96, letterSpacing: '-0.035em', marginBottom: 36 };
const heroLeadStyle = { fontFamily: T.serif, fontSize: 22, fontWeight: 300, lineHeight: 1.5, color: T.creamDim, maxWidth: 580, marginBottom: 44 };
const heroStatsStyle = { display: 'grid', gridTemplateColumns: 'repeat(4, auto)', gap: 40, paddingTop: 36, borderTop: `1px solid ${T.lineSoft}` };
const heroVisualStyle = { position: 'relative', aspectRatio: '4 / 5', overflow: 'hidden', borderRadius: 2 };

const sectionHeadStyle = { display: 'grid', gridTemplateColumns: '0.5fr 1.5fr', gap: 60, marginBottom: 80, alignItems: 'end' };
const sectionTitleStyle = { fontFamily: T.serif, fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 300, lineHeight: 1.05, letterSpacing: '-0.02em' };
const missionBodyStyle = { maxWidth: 820, fontFamily: T.serif, fontSize: 'clamp(22px, 2.4vw, 30px)', fontWeight: 300, lineHeight: 1.5, color: T.cream, letterSpacing: '-0.005em' };

const audienceGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: T.line, border: `1px solid ${T.line}` };
const audienceItemStyle = { background: T.bg, padding: '48px 32px', display: 'flex', flexDirection: 'column', gap: 16, transition: 'background 0.25s ease' };

const toolkitGridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, background: T.line, border: `1px solid ${T.line}` };
const toolStyle = { background: T.bg, padding: '48px 36px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', transition: 'background 0.3s ease' };

const trustGridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' };
const trustImageStyle = { aspectRatio: '4 / 5', overflow: 'hidden', borderRadius: 2, position: 'sticky', top: 100 };

const footerGridStyle = { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 60, marginBottom: 60 };

// ──────────────────────────────────────────────────────────────────────
// Content data
// ──────────────────────────────────────────────────────────────────────
const AUDIENCE = [
  {
    tag: '// 01 · artisans',
    name: 'Artisans',
    desc: 'Creative professionals across every discipline — visual artists, performers, makers, writers, directors, crew. The people who make the work.',
    bullets: ['Verified profile + portfolio', 'Apply to casting calls', 'Get discovered, get hired', 'Studio tier: full Member Tools'],
  },
  {
    tag: '// 02 · venues',
    name: 'Venues',
    desc: 'Theaters, music venues, galleries, production companies, event spaces. The places and organizations that hire creative talent at scale.',
    bullets: ['Verified organizational profile', 'Post casting calls + bookings', 'List on Aisle for discovery', 'Team management at Enterprise'],
  },
  {
    tag: '// 03 · industry pros',
    name: 'Industry Pros',
    desc: 'Casting directors, agents, managers, producers, label reps, brand reps. The people who broker creative careers and build rosters.',
    bullets: ['Unlimited Talent Search', 'Verification Score visibility', 'Shortlists + Contact All', 'Direct booking initiation'],
  },
  {
    tag: '// 04 · spectators',
    name: 'Spectators',
    desc: 'The audience. Free accounts on Aisle for anyone who wants to discover, follow, and support verified creative professionals.',
    bullets: ['Free public account', 'Follow + save Artisans', 'Purchase event tickets', 'Browse without an account'],
  },
];

const TRANSACTIONS = [
  {
    num: '01 / Casting',
    name: 'Casting calls',
    desc: 'Venues post calls. Verified Artisans apply with their authenticated profile and a cover note. Industry Pros browse the applicant pool with full visibility into Verification Scores. The primary way creative work gets matched on Artizan.',
    fee: '12% platform fee on booking',
    who: 'Venues posting open roles, Artisans applying to creative opportunities, Industry Pros sourcing from authenticated talent pools — all on a verification-first foundation.',
  },
  {
    num: '02 / Direct Booking',
    name: 'Direct booking',
    desc: 'When an Industry Pro or Venue has already found the right Artisan, they can book them directly. A structured booking request, payment via Stripe, and contract delivery through BoldSign — no casting call needed.',
    fee: '12% platform fee on booking',
    who: 'Industry Pros with established rosters, Venues with returning collaborators, Artisans with direct client relationships who want everything documented and paid through one place.',
  },
  {
    num: '03 / Marketplace',
    name: 'Marketplace',
    desc: 'Artisans sell directly to their audience — at launch, digital downloads (PDFs, presets, project files, course access). Physical goods, services, and live courses expand in Year 2.',
    fee: '5–10% platform fee (category-dependent)',
    who: 'Artisans monetizing their craft outside booking — photography presets, writing courses, design templates, music samples, sewing patterns, anything downloadable.',
  },
  {
    num: '04 / Live Events',
    name: 'Live events & ticketing',
    desc: 'Artisans and Venues create ticketed events on Aisle. Spectators buy tickets through Stripe. PDF + QR code tickets, single-tier pricing at launch. The audience-facing entry point to the network.',
    fee: '10% platform fee on ticket revenue',
    who: 'Artisans hosting their own shows, Venues selling tickets for live performances, audiences discovering new work on Aisle.',
  },
];

const TOOLS = [
  {
    tag: '// vizion',
    phase: 'Pre-Production',
    name: 'Vizion',
    tagline: 'Plan the work before the work.',
    desc: 'A modular pre-production canvas for any creative project. From a solo gallery show to a 30-person film crew — same tool, adaptive surfaces.',
    features: ['Project brief & mood boards', 'Storyboards & shot lists', 'Budget & timeline', 'Crew briefing & tasks', 'Pitch & treatment builder', 'AI collaborator suggestions'],
  },
  {
    tag: '// krew',
    phase: 'Production',
    name: 'Krew',
    tagline: 'The day the plan becomes the work.',
    desc: 'Production-day tooling for the people running it. Call sheets, run sheets, slate logs, continuity, wrap reports — built for on-set, on-stage, on-runway.',
    features: ['Call sheet builder', 'Daily run sheet', 'Slate / shot log', 'Continuity tracker', 'Crew & talent roster', 'Daily wrap report'],
  },
  {
    tag: '// curtainz',
    phase: 'Post-Production',
    name: 'Curtainz',
    tagline: 'Everything after the wrap.',
    desc: 'A post-production hub that organizes the work other tools produce. Tracking, asset management, EPK builder, captions, distribution. Not a replacement for Premiere or Resolve — a layer above them.',
    features: ['Edit version tracking', 'QC checklist', 'Asset library + cloud links', 'EPK / pitch builder', 'Caption tracking', 'Union flag + portal link-out'],
  },
];

const TRUST = [
  {
    name: 'Identity verification',
    desc: 'Every account is reviewed before it appears on the network. Artisans complete up to eight independent verification methods. Venues complete a three-method organizational verification. Industry Pros are credential-checked. Identity isn\'t a checkbox — it\'s the foundation.',
  },
  {
    name: 'Content moderation',
    desc: 'All user-uploaded imagery is scanned at the point of upload using Microsoft PhotoDNA for known illegal content. Matches are blocked, accounts suspended, and reports filed with NCMEC\'s CyberTipline as required by 18 U.S.C. § 2258A. We are a registered Electronic Service Provider with NCMEC.',
  },
  {
    name: 'DMCA safe harbor',
    desc: 'Artizan Studio LLC is a registered DMCA designated agent with the U.S. Copyright Office (tracking ID 28186K4U). Takedown requests are processed under the protections and procedures of 17 U.S.C. § 512.',
  },
  {
    name: 'Strict 18+',
    desc: 'The platform is strictly for adults aged 18 or older across all products. Age verification is performed server-side at signup. Accounts found to belong to minors are terminated and, where appropriate, reported.',
  },
  {
    name: 'Privacy by design',
    desc: 'We don\'t sell user data. We don\'t run an advertising business. We don\'t use User Content to train generative AI. The platform is funded by membership tiers and transaction fees, and our incentives stay aligned with the people on it.',
  },
  {
    name: 'Owned by you',
    desc: 'Members retain full ownership of all work uploaded to the platform. Artizan takes a limited license to display, distribute, and format your work within the platform — not to own it, train against it, or relicense it elsewhere.',
  },
];
